import React from 'react';

const FeatureCard = ({ featureName, featureData, colors }) => {
  const { human_readable_name, percentage, raw_value } = featureData;

  // Get performance level based on percentage
  const getPerformanceLevel = (percent) => {
    if (percent >= 80) return { level: 'Excellent', color: 'green' };
    if (percent >= 60) return { level: 'Good', color: 'blue' };
    if (percent >= 40) return { level: 'Average', color: 'yellow' };
    if (percent >= 20) return { level: 'Below Average', color: 'orange' };
    return { level: 'Needs Improvement', color: 'red' };
  };

  const performance = getPerformanceLevel(percentage);

  // Format raw value for display
  const formatRawValue = (value) => {
    if (typeof value === 'number') {
      if (value % 1 === 0) return value.toString();
      return value.toFixed(2);
    }
    return value?.toString() || 'N/A';
  };

  // Create dynamic styles to avoid Tailwind purging issues
  const cardStyle = {
    borderColor: colors.primary === 'emerald' ? '#10b981' :
                 colors.primary === 'blue' ? '#3b82f6' :
                 colors.primary === 'purple' ? '#8b5cf6' :
                 colors.primary === 'orange' ? '#f97316' :
                 colors.primary === 'red' ? '#ef4444' : '#6b7280'
  };

  const titleColor = colors.primary === 'emerald' ? 'text-emerald-800' :
                     colors.primary === 'blue' ? 'text-blue-800' :
                     colors.primary === 'purple' ? 'text-purple-800' :
                     colors.primary === 'orange' ? 'text-orange-800' :
                     colors.primary === 'red' ? 'text-red-800' : 'text-gray-800';

  const percentageColor = colors.primary === 'emerald' ? 'text-emerald-700' :
                          colors.primary === 'blue' ? 'text-blue-700' :
                          colors.primary === 'purple' ? 'text-purple-700' :
                          colors.primary === 'orange' ? 'text-orange-700' :
                          colors.primary === 'red' ? 'text-red-700' : 'text-gray-700';

  const valueColor = colors.primary === 'emerald' ? 'text-emerald-600' :
                     colors.primary === 'blue' ? 'text-blue-600' :
                     colors.primary === 'purple' ? 'text-purple-600' :
                     colors.primary === 'orange' ? 'text-orange-600' :
                     colors.primary === 'red' ? 'text-red-600' : 'text-gray-600';

  const performanceBadgeStyle = {
    backgroundColor: performance.color === 'green' ? '#dcfce7' :
                     performance.color === 'blue' ? '#dbeafe' :
                     performance.color === 'yellow' ? '#fef3c7' :
                     performance.color === 'orange' ? '#fed7aa' :
                     performance.color === 'red' ? '#fecaca' : '#f3f4f6',
    color: performance.color === 'green' ? '#166534' :
           performance.color === 'blue' ? '#1e40af' :
           performance.color === 'yellow' ? '#92400e' :
           performance.color === 'orange' ? '#c2410c' :
           performance.color === 'red' ? '#dc2626' : '#374151'
  };

  const gradientStyle = {
    background: colors.primary === 'emerald' ? 'linear-gradient(to right, #34d399, #10b981)' :
                colors.primary === 'blue' ? 'linear-gradient(to right, #60a5fa, #3b82f6)' :
                colors.primary === 'purple' ? 'linear-gradient(to right, #a78bfa, #8b5cf6)' :
                colors.primary === 'orange' ? 'linear-gradient(to right, #fb923c, #f97316)' :
                colors.primary === 'red' ? 'linear-gradient(to right, #f87171, #ef4444)' :
                'linear-gradient(to right, #9ca3af, #6b7280)',
    width: `${Math.min(percentage, 100)}%`
  };

  const indicatorStyle = {
    backgroundColor: performance.color === 'green' ? '#4ade80' :
                     performance.color === 'blue' ? '#60a5fa' :
                     performance.color === 'yellow' ? '#fbbf24' :
                     performance.color === 'orange' ? '#fb923c' :
                     performance.color === 'red' ? '#f87171' : '#9ca3af'
  };

  return (
    <div className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow duration-200" style={cardStyle}>
      <div className="flex items-start justify-between mb-3">
        <h5 className={`text-sm font-medium leading-tight ${titleColor}`}>
          {human_readable_name}
        </h5>
        <div className="px-2 py-1 rounded-full text-xs font-medium" style={performanceBadgeStyle}>
          {performance.level}
        </div>
      </div>

      {/* Percentage Bar */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600">Percentile</span>
          <span className={`text-sm font-semibold ${percentageColor}`}>
            {(+percentage).toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-500 ease-out"
            style={gradientStyle}
          />
        </div>
      </div>

      {/* Raw Value */}
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-500">Raw Value:</span>
        <span className={`font-medium ${valueColor}`}>
          {formatRawValue(raw_value)}
        </span>
      </div>

      {/* Performance Indicator */}
      <div className="mt-2 flex items-center">
        <div className="w-2 h-2 rounded-full mr-2" style={indicatorStyle} />
        <span className="text-xs text-gray-600">
          {percentage >= 50 ? 'Above average performance' : 'Below average performance'}
        </span>
      </div>
    </div>
  );
};

export default FeatureCard;
